import { createClient } from "@sanity/client";
import imageUrlBuilder from "@sanity/image-url";
import type { SanityImageSource } from "@sanity/image-url";

// // // //

export const client = createClient({
    projectId: "xj12fquv", // TODO - pull into env variable
    dataset: "production",
    apiVersion: "2024-02-07",
    useCdn: true, // Set to false if you want to ensure fresh data
});

// Category metadata keyed by the slug-style value Sanity stores in category.title
// (e.g. "update", "guest-post", "tokenized-asset-coalition-2"). Maps each to a
// human-readable display label and the badge background color.
const CATEGORY_META: Record<string, { label: string; color: string }> = {
    research: { label: "Research", color: "#ffeb99" },
    update: { label: "Product Update", color: "#ffad99" },
    coalition: { label: "Coalition", color: "#2cbec9" },
    "tokenized-asset-coalition": {
        label: "Tokenized Asset Coalition",
        color: "hsl(0, 0%, 83%)",
    },
    "tokenized-asset-coalition-2": {
        label: "Tokenized Asset Coalition",
        color: "hsl(0, 0%, 83%)",
    },
    "guest-post": { label: "Guest Post", color: "hsl(12, 100%, 80%)" },
};

// Title-case a slug as a fallback when a category isn't in CATEGORY_META.
function formatCategoryFallback(value: string) {
    return value
        .replace(/-\d+$/, "")
        .split("-")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}

function getCategoryMeta(categoryTitle: string | undefined) {
    const meta = categoryTitle ? CATEGORY_META[categoryTitle] : undefined;
    return {
        label: meta?.label || formatCategoryFallback(categoryTitle || ""),
        color: meta?.color || "#e5e7eb",
    };
}

// Helper function to generate image URLs
const builder = imageUrlBuilder(client);

export function urlFor(source: SanityImageSource) {
    return builder.image(source);
}

// TypeScript types for our blog posts
export interface BlogPost {
    _id: string;
    _createdAt: string;
    title: string;
    slug: {
        current: string;
    };
    excerpt?: string;
    publishedAt?: string;
    readTime?: string;
    featured?: boolean;
    archived?: boolean;
    author: {
        name: string;
        role?: string;
        image?: SanityImageSource;
    };
    category: {
        title: string;
        color?: string;
    };
    thumbnail?: SanityImageSource;
    featureImage?: SanityImageSource;
    categoryColor: string;
    categoryLabel: string;
    body?: any; // Portable text content
}

// Query to get all blog posts with populated references.
// Archived posts are excluded by default; pass `includeArchived` to keep them
// (e.g. for generating static paths so archived posts remain routable).
export async function getAllPosts({
    includeArchived = false,
}: { includeArchived?: boolean } = {}): Promise<BlogPost[]> {
    const archivedFilter = includeArchived ? "" : " && archived != true";
    const query = `*[_type == "blogPost"${archivedFilter}] | order(coalesce(publishedAt, _createdAt) desc) {
    _id,
    _createdAt,
    title,
    slug,
    excerpt,
    publishedAt,
    readTime,
    featured,
    archived,
    "author": author->{name, role, "image": avatar},
    "category": category->{title, color},
    thumbnail,
    featureImage,
    body
  }`;

    const posts = await client.fetch(query);
    return posts.map((post: BlogPost) => {
        const { label, color } = getCategoryMeta(post.category?.title);
        return { ...post, categoryColor: color, categoryLabel: label };
    });
}

// Query to get a single post by slug
export async function getPostBySlug(slug: string): Promise<BlogPost | null> {
    const query = `*[_type == "blogPost" && slug.current == $slug][0] {
    _id,
    _createdAt,
    title,
    slug,
    excerpt,
    publishedAt,
    readTime,
    featured,
    "author": author->{name, role, "image": avatar},
    "category": category->{title, color},
    thumbnail,
    featureImage,
    body
  }`;

    const post = await client.fetch(query, { slug });

    if (!post) {
        return null;
    }

    const { label, color } = getCategoryMeta(post.category?.title);
    return { ...post, categoryColor: color, categoryLabel: label };
}

// Helper to format dates
export function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

// Helper to get the right image (prefer featureImage, fallback to thumbnail)
export function getPostImage(post: BlogPost): SanityImageSource | null {
    return post.featureImage || post.thumbnail || null;
}
